import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from './cn';

type Variant = 'primary' | 'secondary' | 'secondary-light' | 'light';

const base =
  'group relative inline-flex items-center justify-center overflow-hidden rounded-[2px] ' +
  'px-8 text-[0.875rem] font-semibold uppercase tracking-[0.094rem] leading-[1.375rem] ' +
  'transition-colors duration-300 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charcoal';

const variants: Record<Variant, string> = {
  primary: 'h-12 bg-brand text-charcoal hover:bg-brand-dark hover:text-white active:bg-brand-darker active:text-white',
  light: 'h-12 bg-white text-charcoal hover:bg-line',
  secondary: 'h-12 border border-brand text-charcoal hover:border-brand-dark hover:text-white',
  'secondary-light': 'h-12 border border-white text-white hover:text-charcoal',
};

// Fond qui monte du bas au survol (boutons « secondaires »)
const fill: Partial<Record<Variant, string>> = {
  secondary: 'bg-brand-dark',
  'secondary-light': 'bg-white',
};

type Common = { variant?: Variant; className?: string; children: ReactNode; to?: string };
type Props = Common &
  (Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> &
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>);

export default function Button({ variant = 'primary', to, href, className, children, ...rest }: Props) {
  const cls = cn(base, variants[variant], className);
  const inner = (
    <>
      {fill[variant] && (
        <span
          aria-hidden
          className={cn(
            'absolute inset-0 -z-0 translate-y-full transition-transform duration-300 ease-out group-hover:translate-y-0',
            fill[variant],
          )}
        />
      )}
      <span className="relative z-10">{children}</span>
    </>
  );

  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  if (href) return <a href={href} className={cls} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>{inner}</a>;
  return <button className={cls} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>{inner}</button>;
}
